/**
 * Admin Agent Releases RPC definitions
 *
 * Operator endpoints for the clawd versioning model:
 *
 *  - `admin.agentReleases.list` — paginate `agent_releases`
 *  - `admin.agentReleases.publish` — register a built artifact
 *  - `admin.agentReleases.setChannelCurrent` — promote a release into
 *    a channel/platform pointer
 *  - `admin.agentReleases.setChannelPaused` — toggle the kill switch
 *  - `admin.agentReleases.pinInstallVersion` — set/clear the per-
 *    install version pin
 *  - `admin.agentReleases.setInstallChannel` — set/clear the per-
 *    install channel override
 *
 * All endpoints require admin role via {@link AdminMiddleware}.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { AgentChannelName } from "@fm/db/models/AgentChannel";
import { AgentChannelHistory } from "@fm/db/models/AgentChannelHistory";
import { AgentInstallation } from "@fm/db/models/AgentInstallation";
import { AgentPlatform, AgentRelease } from "@fm/db/models/AgentRelease";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import {
  DatabaseError,
  Forbidden,
  RecordNotFoundError,
  RemoteSupportNotEnabled,
  Unauthorized,
  ValkeyError,
} from "../errors.ts";

// ============================================================================
// Errors
// ============================================================================

/**
 * Returned by `pinInstallVersion` when the requested version has no
 * `agent_releases` row for any platform — keeps operators from pinning
 * an install to a typo'd version that nothing serves.
 *
 * @since 1.0.0
 */
export class AgentReleaseVersionNotFoundError extends Schema.TaggedError<AgentReleaseVersionNotFoundError>()(
  "AgentReleaseVersionNotFoundError",
  {
    version: Schema.String,
    message: Schema.String,
  },
) {}

/**
 * Returned by `forceCheckUpdate` when the agent didn't report an
 * outcome within the 60-second budget. Callers retry or wait for the
 * agent's next scheduled cycle.
 *
 * @since 1.0.0
 */
export class ForceCheckUpdateTimeoutError extends Schema.TaggedError<ForceCheckUpdateTimeoutError>()(
  "ForceCheckUpdateTimeoutError",
  {
    installationId: Schema.String,
    message: Schema.String,
  },
) {}

// ============================================================================
// Request schemas
// ============================================================================

/**
 * Pagination + filter input for `list`. `cursor` is the
 * `created_at` epoch-millis returned by a prior page.
 *
 * @since 1.0.0
 */
export const AgentReleasesListInput = Schema.Struct({
  platform: Schema.optional(AgentPlatform),
  limit: Schema.optionalWith(Schema.Number, { default: () => 50 }),
  cursor: Schema.optional(Schema.String),
});

/**
 * Idempotent publish input. Driven by the GH Actions publisher in
 * Phase 2 — Phase 1 only stores the row.
 *
 * @since 1.0.0
 */
export const AgentReleasesPublishInput = Schema.Struct({
  version: Schema.NonEmptyTrimmedString,
  platform: AgentPlatform,
  downloadUrl: Schema.NonEmptyTrimmedString,
  sha256: Schema.NonEmptyTrimmedString,
  sizeBytes: Schema.Number,
  notes: Schema.optional(Schema.String),
});

/**
 * Promote-a-release input. Atomically updates the (channel, platform)
 * row AND appends a row to `agent_channel_history`.
 *
 * @since 1.0.0
 */
export const AgentReleasesSetChannelCurrentInput = Schema.Struct({
  channel: AgentChannelName,
  platform: AgentPlatform,
  releaseId: Schema.NonEmptyTrimmedString,
  reason: Schema.optional(Schema.String),
});

/**
 * Pause / resume input.
 *
 * @since 1.0.0
 */
export const AgentReleasesSetChannelPausedInput = Schema.Struct({
  channel: AgentChannelName,
  platform: AgentPlatform,
  paused: Schema.Boolean,
  reason: Schema.optional(Schema.String),
});

/**
 * Per-install version pin input. `version: null` clears the pin.
 *
 * @since 1.0.0
 */
export const AgentReleasesPinInstallVersionInput = Schema.Struct({
  installationId: Schema.NonEmptyTrimmedString,
  version: Schema.NullOr(Schema.NonEmptyTrimmedString),
});

/**
 * Per-install channel override input. `channel: null` clears the
 * override (install reverts to the default `stable`).
 *
 * @since 1.0.0
 */
export const AgentReleasesSetInstallChannelInput = Schema.Struct({
  installationId: Schema.NonEmptyTrimmedString,
  channel: Schema.NullOr(AgentChannelName),
});

/**
 * `nextVersion` takes no input — the server reads the current UTC date
 * and queries today's already-published versions itself.
 *
 * @since 1.0.0
 */
export const AgentReleasesNextVersionInput = Schema.Struct({});

/**
 * Force-check input: enqueue an `agent.checkUpdate` command on the
 * install's CommandStream and wait up to 60s for the outcome.
 *
 * @since 1.0.0
 */
export const AgentReleasesForceCheckUpdateInput = Schema.Struct({
  installationId: Schema.NonEmptyTrimmedString,
});

/**
 * Tagged outcome the agent reports back via heartbeat.
 *
 * @since 1.0.0
 */
export const AgentReleasesForceCheckUpdateOutcome = Schema.Literal(
  "up-to-date",
  "swapped",
  "paused",
  "failed:sha256",
  "failed:download",
  // CLA-724: filesystem / permission failure during the swap, distinct from
  // `failed:download`.
  "failed:filesystem",
  "failed:manifest-not-found",
  "failed:manifest-parse",
  "failed:network",
);
export type AgentReleasesForceCheckUpdateOutcome = Schema.Schema.Type<
  typeof AgentReleasesForceCheckUpdateOutcome
>;

/**
 * `forceCheckUpdate` response — a tagged outcome plus the version the
 * manifest pointed at when the cycle ran.
 *
 * @since 1.0.0
 */
export const AgentReleasesForceCheckUpdateResponse = Schema.Struct({
  outcome: AgentReleasesForceCheckUpdateOutcome,
  targetVersion: Schema.NullOr(Schema.String),
});
export type AgentReleasesForceCheckUpdateResponse = Schema.Schema.Type<
  typeof AgentReleasesForceCheckUpdateResponse
>;

// ============================================================================
// Response schemas
// ============================================================================

/**
 * Paginated `list` response.
 *
 * @since 1.0.0
 */
export const AgentReleasesListResponse = Schema.Struct({
  rows: Schema.Array(AgentRelease.json),
  nextCursor: Schema.NullOr(Schema.String),
});
export type AgentReleasesListResponse = Schema.Schema.Type<typeof AgentReleasesListResponse>;

/**
 * Channel-row response shape returned by setChannelCurrent /
 * setChannelPaused.
 *
 * @since 1.0.0
 */
/**
 * `nextVersion` response — the CalVer string the publisher should
 * tag the next release with.
 *
 * @since 1.0.0
 */
export const AgentReleasesNextVersionResponse = Schema.Struct({
  version: Schema.NonEmptyTrimmedString,
});
export type AgentReleasesNextVersionResponse = Schema.Schema.Type<
  typeof AgentReleasesNextVersionResponse
>;

export const AgentChannelResponse = Schema.Struct({
  id: Schema.String,
  channel: AgentChannelName,
  platform: AgentPlatform,
  currentReleaseId: Schema.NullOr(Schema.String),
  paused: Schema.Boolean,
  updatedAt: Schema.DateTimeUtc,
});
export type AgentChannelResponse = Schema.Schema.Type<typeof AgentChannelResponse>;

/**
 * Channel-pointer row enriched with the resolved version string from
 * the joined `agent_releases` row. Backs the agent-releases page's
 * channel pointers grid (3×3).
 *
 * @since 1.0.0
 */
export const AgentChannelWithVersionResponse = Schema.Struct({
  id: Schema.String,
  channel: AgentChannelName,
  platform: AgentPlatform,
  currentReleaseId: Schema.NullOr(Schema.String),
  currentVersion: Schema.NullOr(Schema.String),
  paused: Schema.Boolean,
  updatedAt: Schema.DateTimeUtc,
});
export type AgentChannelWithVersionResponse = Schema.Schema.Type<
  typeof AgentChannelWithVersionResponse
>;

/**
 * `listChannels` takes no input — returns all 9 (channel, platform)
 * rows pre-seeded by the migration. Used by the operator UI to render
 * the channel-pointers grid.
 *
 * @since 1.0.0
 */
export const AgentReleasesListChannelsInput = Schema.Struct({});

/**
 * `listChannels` response. Always 9 rows when the migration ran.
 *
 * @since 1.0.0
 */
export const AgentReleasesListChannelsResponse = Schema.Struct({
  rows: Schema.Array(AgentChannelWithVersionResponse),
});
export type AgentReleasesListChannelsResponse = Schema.Schema.Type<
  typeof AgentReleasesListChannelsResponse
>;

/**
 * Pagination + filter input for `channelHistory`. `cursor` is the
 * `changed_at` epoch-millis of the last row from a prior page.
 *
 * @since 1.0.0
 */
export const AgentReleasesChannelHistoryInput = Schema.Struct({
  channel: Schema.optional(AgentChannelName),
  platform: Schema.optional(AgentPlatform),
  limit: Schema.optionalWith(Schema.Number, { default: () => 50 }),
  cursor: Schema.optional(Schema.String),
});

/**
 * Paginated `channelHistory` response.
 *
 * @since 1.0.0
 */
export const AgentReleasesChannelHistoryResponse = Schema.Struct({
  rows: Schema.Array(AgentChannelHistory.json),
  nextCursor: Schema.NullOr(Schema.String),
});
export type AgentReleasesChannelHistoryResponse = Schema.Schema.Type<
  typeof AgentReleasesChannelHistoryResponse
>;

/**
 * `distribution` takes no input — the operator UI applies channel /
 * platform filters client-side from the full result set. Aggregation
 * is done server-side so the browser never sees the raw install rows.
 *
 * @since 1.0.0
 */
export const AgentReleasesDistributionInput = Schema.Struct({});

/**
 * One row in the distribution group-by output.
 *
 * @since 1.0.0
 */
export const AgentReleasesDistributionRow = Schema.Struct({
  channel: AgentChannelName,
  platform: AgentPlatform,
  version: Schema.NullOr(Schema.String),
  count: Schema.Number,
});
export type AgentReleasesDistributionRow = Schema.Schema.Type<
  typeof AgentReleasesDistributionRow
>;

/**
 * One row in the stragglers list — installs whose `agent_version`
 * doesn't match the channel pointer they should be following.
 *
 * @since 1.0.0
 */
export const AgentReleasesStragglerRow = Schema.Struct({
  installationId: Schema.String,
  installationName: Schema.String,
  customerId: Schema.NullOr(Schema.String),
  channel: AgentChannelName,
  platform: AgentPlatform,
  currentVersion: Schema.NullOr(Schema.String),
  targetVersion: Schema.NullOr(Schema.String),
  lastUpdateCheckAt: Schema.NullOr(Schema.DateTimeUtc),
  lastUpdateOutcome: Schema.NullOr(Schema.String),
});
export type AgentReleasesStragglerRow = Schema.Schema.Type<
  typeof AgentReleasesStragglerRow
>;

/**
 * `distribution` response.
 *
 * @since 1.0.0
 */
export const AgentReleasesDistributionResponse = Schema.Struct({
  rows: Schema.Array(AgentReleasesDistributionRow),
  stragglers: Schema.Array(AgentReleasesStragglerRow),
});
export type AgentReleasesDistributionResponse = Schema.Schema.Type<
  typeof AgentReleasesDistributionResponse
>;

// ============================================================================
// RPC group
// ============================================================================

/**
 * Admin Agent Releases RPC group. All endpoints require admin role
 * via {@link AdminMiddleware}.
 *
 * @since 1.0.0
 */
export class AdminAgentReleasesRpcs extends RpcGroup.make(
  Rpc.make("admin.agentReleases.list", {
    payload: AgentReleasesListInput,
    success: AgentReleasesListResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.agentReleases.nextVersion", {
    payload: AgentReleasesNextVersionInput,
    success: AgentReleasesNextVersionResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.agentReleases.publish", {
    payload: AgentReleasesPublishInput,
    success: AgentRelease.json,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.agentReleases.setChannelCurrent", {
    payload: AgentReleasesSetChannelCurrentInput,
    success: AgentChannelResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.agentReleases.setChannelPaused", {
    payload: AgentReleasesSetChannelPausedInput,
    success: AgentChannelResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.agentReleases.pinInstallVersion", {
    payload: AgentReleasesPinInstallVersionInput,
    success: AgentInstallation.json,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      DatabaseError,
      RecordNotFoundError,
      AgentReleaseVersionNotFoundError,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.agentReleases.setInstallChannel", {
    payload: AgentReleasesSetInstallChannelInput,
    success: AgentInstallation.json,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, RecordNotFoundError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.agentReleases.forceCheckUpdate", {
    payload: AgentReleasesForceCheckUpdateInput,
    success: AgentReleasesForceCheckUpdateResponse,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      DatabaseError,
      RecordNotFoundError,
      RemoteSupportNotEnabled,
      ValkeyError,
      ForceCheckUpdateTimeoutError,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.agentReleases.listChannels", {
    payload: AgentReleasesListChannelsInput,
    success: AgentReleasesListChannelsResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.agentReleases.channelHistory", {
    payload: AgentReleasesChannelHistoryInput,
    success: AgentReleasesChannelHistoryResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.agentReleases.distribution", {
    payload: AgentReleasesDistributionInput,
    success: AgentReleasesDistributionResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
